import React, { useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import { Inline } from '@atlaskit/primitives/compiled';
import { EntityType, type UserSearchItem } from '@atlaskit/smart-common';
import { isTeamsAppEnabled } from '@atlaskit/teams-app-config/is-teams-app-enabled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import {
	getGenericUrlWithAnalyticsQueryParams,
	getTeamPath,
	getUserPath,
	highlightMatchedText,
} from '@atlassian/search-common';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { useAppContext } from '../../../../common/ui/app-context';
import { useItemsAnalyticsContext } from '../../../../common/ui/quick-find/items-analytics-provider';
import {
	SearchResult,
	type SearchResultEventArguments,
} from '../../../../common/ui/quick-find/search-dialog-result';
import { type SearchMetadataPart } from '../../../../common/ui/search-metadata';
import { type State } from '../../../../common/utils/quick-find/state';
import { type QuickFindResultEvent } from '../../../../controllers/quick-find/utils';
import {
	useCloudId,
	useOrgId,
	usePrimaryProduct,
	useSearchSessionId,
} from '../../../../controllers/store';
import { useQuickFindQuery } from '../../../../controllers/store/quick-find';

import { messages } from './messages';

type Props = {
	result: UserSearchItem;
	state: State;
	onClick?: (props: QuickFindResultEvent) => void;
	onContextMenu?: (props: QuickFindResultEvent) => void;
	onHighlighted?: (props: QuickFindResultEvent) => void;
};

export const PeopleAndTeams = ({ result, state, onClick, onContextMenu, onHighlighted }: Props) => {
	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const [primaryProduct] = usePrimaryProduct();
	const quickFindQuery = useQuickFindQuery();
	const [searchSessionId] = useSearchSessionId();
	const { generateTeamUrl, generateUserUrl } = useAppContext();
	const { invalidateItem } = useItemsAnalyticsContext();

	const id = result.id;

	const userTitle = result.name;
	const teamTitle = result.displayName;
	const title = result.entityType === EntityType.USER ? userTitle : teamTitle;

	const highlightedTitle =
		title && quickFindQuery
			? highlightMatchedText(title, quickFindQuery.split(' ')).highlightedTitle
			: undefined;

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	const icon = (
		<Avatar
			appearance={result.entityType === EntityType.USER ? 'circle' : 'square'}
			borderColor="transparent"
			name={title}
			size={'small'}
			src={result.avatarUrl || result.smallAvatarImageUrl}
		/>
	);

	const userMetadata = ['user-metadata', result.title] satisfies SearchMetadataPart;
	const teamMetadata = [
		'team-metadata',
		<FormattedMessage {...messages.team} />,
	] satisfies SearchMetadataPart;
	const metadata = result.entityType === EntityType.USER ? [userMetadata] : [teamMetadata];

	// Avoiding typeguards
	const hostProductForTeamsApp =
		primaryProduct === 'jira' ? 'jira' : primaryProduct === 'confluence' ? 'confluence' : undefined;

	let url;
	let onNavigate:
		| ((event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) => void)
		| undefined;
	if (
		isTeamsAppEnabled({
			hostProduct: hostProductForTeamsApp,
		})
	) {
		const { href, onNavigate: onNavigateTeamsApp } = navigateToTeamsApp(
			result.entityType === EntityType.USER
				? {
						type: 'USER',
						payload: {
							userId: result.id,
						},
						cloudId: cloudId || '',
						orgId,
					}
				: {
						type: 'TEAM',
						payload: {
							teamId: result.id,
						},
						cloudId: cloudId || '',
						orgId,
					},
		);
		url = href;
		onNavigate = onNavigateTeamsApp;
	} else {
		let path;
		// TODO remove this statement once all products have specified their own generateUserUrl and generateTeamUrl props
		if (primaryProduct && orgId && cloudId) {
			path =
				result.entityType === EntityType.USER
					? getUserPath(result.id, primaryProduct, orgId, cloudId)
					: getTeamPath(result.id, primaryProduct, orgId, cloudId);
		}

		if (generateUserUrl && result.entityType === EntityType.USER) {
			path = generateUserUrl(result.id);
		} else if (generateTeamUrl && result.entityType !== EntityType.USER) {
			path = generateTeamUrl(result.id);
		}

		url = path && new URL(path, window.location.origin).href;
	}

	const analyticsMetadata = useMemo(
		() => ({
			contentId: id,
			contentType: 'user',
		}),
		[id],
	);

	const onResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onClick?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes: {
					metadata: analyticsMetadata,
				},
			});
			if (
				onNavigate &&
				isTeamsAppEnabled({
					hostProduct: hostProductForTeamsApp,
				})
			) {
				// Handles opening the link in a new tab
				onNavigate(event);
			}
		},
		[analyticsMetadata, onClick, onNavigate, state, hostProductForTeamsApp],
	);

	const onResultContextMenu = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onContextMenu?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes: {
					metadata: analyticsMetadata,
				},
			});
		},
		[analyticsMetadata, onContextMenu, state],
	);

	const onResultHighlighted = useCallback(
		(data: SearchResultEventArguments) => {
			onHighlighted?.({
				data,
				state,
				additionalAnalyticsAttributes: {
					metadata: analyticsMetadata,
				},
			});
		},
		[analyticsMetadata, onHighlighted, state],
	);

	if (!title || !metadata || !url) {
		invalidateItem(result.id);
		return null;
	}

	const urlWithAnalyticsQueryParams = getGenericUrlWithAnalyticsQueryParams({
		url,
		searchSessionId,
	});

	return (
		<SearchResult
			id={id}
			icon={icon}
			resultType="peopleAndTeams"
			title={title}
			highlightedTitle={highlightedTitle}
			metadata={metadata}
			highlightedRightMetadata={highlightedRightMetadata}
			url={urlWithAnalyticsQueryParams}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onResultHighlighted={onResultHighlighted}
		/>
	);
};
